import { chain, setIn } from 'icepick';
import isNil from 'lodash/isNil';
import type { Action, StoreActionApi } from 'react-sweet-state';
import {
	TEXT_FIELDTYPE,
	type ThirdPartyFieldId,
	type Field,
	type FieldId,
} from '../../../../common/constants/fields';
import type {
	State,
	ThirdPartyFieldConfigStateForItem,
	ThirdPartyFieldConfigState,
} from '../../../../common/types';

export type SetThirdPartyItemTypeFieldMappingAction = (
	fieldId: ThirdPartyFieldId,
	jiraField: Field,
) => Action<State, void, void>;

export type SetThirdPartyFieldMappingAction = (
	itemType: string | null,
	fieldId: ThirdPartyFieldId,
	jiraField: Field,
) => Action<State, void, void>;

export const getNewMappedJiraFields = (
	thirdPartyFieldConfigState: ThirdPartyFieldConfigStateForItem,
	thirdPartyFieldId: ThirdPartyFieldId,
	newJiraField: Field,
) => {
	const newMappedJiraFields = { ...thirdPartyFieldConfigState.mappedJiraFields };
	const newMappedJiraFieldId = newJiraField.fieldId;
	const prevMappedJiraField =
		thirdPartyFieldConfigState.thirdPartyFieldMappings[thirdPartyFieldId].mappedJiraField;

	// remove the previously mappedJiraField
	if (prevMappedJiraField.fieldId) {
		delete newMappedJiraFields[prevMappedJiraField.fieldId];
	}

	// add the new mappedJira Field
	if (!isNil(newMappedJiraFieldId)) {
		newMappedJiraFields[newMappedJiraFieldId] = thirdPartyFieldId;
	}

	return newMappedJiraFields;
};

export const getNewMappedJiraFieldsWithItemType = (
	itemType: string,
	thirdPartyFieldConfigState: ThirdPartyFieldConfigState,
	thirdPartyFieldId: ThirdPartyFieldId,
	newJiraField: Field,
) => {
	const newMappedJiraFields = {
		...thirdPartyFieldConfigState.fieldMappingState[itemType].mappedJiraFields,
	};
	const newMappedJiraFieldId = newJiraField.fieldId;
	const prevMappedJiraField =
		thirdPartyFieldConfigState.fieldMappingState[itemType].thirdPartyFieldMappings[
			thirdPartyFieldId
		].mappedJiraField;

	// remove the previously mappedJiraField
	if (prevMappedJiraField.fieldId) {
		delete newMappedJiraFields[prevMappedJiraField.fieldId];
	}

	// add the new mappedJira Field
	if (!isNil(newMappedJiraFieldId)) {
		newMappedJiraFields[newMappedJiraFieldId] = thirdPartyFieldId;
	}

	return newMappedJiraFields;
};

export const getNewJiraFieldForExcludedThirdPartyField = (
	thirdPartyFieldConfigState: ThirdPartyFieldConfigStateForItem,
	prevMappedThirdPartyFieldId: ThirdPartyFieldId,
) => {
	const prevMappedThirdPartyFieldMapping =
		thirdPartyFieldConfigState.thirdPartyFieldMappings[prevMappedThirdPartyFieldId];

	// defaulting to text field if there are no compatible new field
	const prevFirstCompatibleNextFieldType =
		prevMappedThirdPartyFieldMapping.compatibleNewFieldTypes[0] ?? TEXT_FIELDTYPE;

	const newMappedJiraField: Field = {
		fieldType: prevFirstCompatibleNextFieldType,
		fieldName: prevMappedThirdPartyFieldMapping.thirdPartyField.fieldName,
	};
	return newMappedJiraField;
};

export const getNewJiraFieldForExcludedThirdPartyFieldWithItemType = (
	itemType: string,
	thirdPartyFieldConfigState: ThirdPartyFieldConfigState,
	prevMappedThirdPartyFieldId: ThirdPartyFieldId,
) => {
	const prevMappedThirdPartyFieldMapping =
		thirdPartyFieldConfigState.fieldMappingState[itemType].thirdPartyFieldMappings[
			prevMappedThirdPartyFieldId
		];

	// defaulting to text field if there are no compatible new field
	const prevFirstCompatibleNextFieldType =
		prevMappedThirdPartyFieldMapping.compatibleNewFieldTypes[0] ?? TEXT_FIELDTYPE;

	const newMappedJiraField: Field = {
		fieldType: prevFirstCompatibleNextFieldType,
		fieldName: prevMappedThirdPartyFieldMapping.thirdPartyField.fieldName,
	};
	return newMappedJiraField;
};

export const getNewIncludedJiraFieldIds = (
	thirdPartyFieldConfigState: ThirdPartyFieldConfigStateForItem,
	thirdPartyFieldId: ThirdPartyFieldId,
	newJiraField: Field,
): Set<FieldId> => {
	const newIncludedJiraFieldIds = new Set([...thirdPartyFieldConfigState.includedJiraFieldIds]);
	const prevMappedJiraField =
		thirdPartyFieldConfigState.thirdPartyFieldMappings[thirdPartyFieldId].mappedJiraField;
	if (prevMappedJiraField.fieldId) {
		newIncludedJiraFieldIds.delete(prevMappedJiraField.fieldId);
	}
	if (newJiraField.fieldId) {
		newIncludedJiraFieldIds.add(newJiraField.fieldId);
	}
	return newIncludedJiraFieldIds;
};

export const getNewIncludedJiraFieldIdsWithItemType = (
	itemType: string,
	thirdPartyFieldConfigState: ThirdPartyFieldConfigState,
	thirdPartyFieldId: ThirdPartyFieldId,
	newJiraField: Field,
): Set<FieldId> => {
	const newIncludedJiraFieldIds = new Set([
		...thirdPartyFieldConfigState.fieldMappingState[itemType].includedJiraFieldIds,
	]);
	const prevMappedJiraField =
		thirdPartyFieldConfigState.fieldMappingState[itemType].thirdPartyFieldMappings[
			thirdPartyFieldId
		].mappedJiraField;
	if (prevMappedJiraField.fieldId) {
		newIncludedJiraFieldIds.delete(prevMappedJiraField.fieldId);
	}
	if (newJiraField.fieldId) {
		newIncludedJiraFieldIds.add(newJiraField.fieldId);
	}
	return newIncludedJiraFieldIds;
};

export const setThirdPartyFieldMapping: SetThirdPartyFieldMappingAction =
	(itemType: string | null, fieldId: ThirdPartyFieldId, jiraField: Field) =>
	({ setState, getState }: StoreActionApi<State>) => {
		const state = getState();
		if (itemType === null || state.thirdPartyState.thirdPartyFieldConfigState === null) {
			return;
		}
		const prevMappedThirdPartyFieldId = jiraField.fieldId
			? state.thirdPartyState.thirdPartyFieldConfigState.fieldMappingState[itemType]
					.mappedJiraFields[jiraField.fieldId]
			: undefined;
		let newState = chain(state)
			.setIn(
				[
					'thirdPartyState',
					'thirdPartyFieldConfigState',
					'fieldMappingState',
					itemType,
					'thirdPartyFieldMappings',
					fieldId,
					'mappedJiraField',
				],
				jiraField,
			)
			.setIn(
				[
					'thirdPartyState',
					'thirdPartyFieldConfigState',
					'fieldMappingState',
					itemType,
					'mappedJiraFields',
				],
				getNewMappedJiraFieldsWithItemType(
					itemType,
					state.thirdPartyState.thirdPartyFieldConfigState,
					fieldId,
					jiraField,
				),
			)
			.setIn(
				[
					'thirdPartyState',
					'thirdPartyFieldConfigState',
					'fieldMappingState',
					itemType,
					'includedJiraFieldIds',
				],
				getNewIncludedJiraFieldIdsWithItemType(
					itemType,
					state.thirdPartyState.thirdPartyFieldConfigState,
					fieldId,
					jiraField,
				),
			)
			.value();

		// replace the mapped jira field of any excluded third party fields that are currently mapped to the jiraField
		// we replace it with a new jira field using the first compatibleNewFieldType
		if (prevMappedThirdPartyFieldId) {
			newState = setIn(
				newState,
				[
					'thirdPartyState',
					'thirdPartyFieldConfigState',
					'fieldMappingState',
					itemType,
					'thirdPartyFieldMappings',
					prevMappedThirdPartyFieldId,
					'mappedJiraField',
				],
				getNewJiraFieldForExcludedThirdPartyFieldWithItemType(
					itemType,
					state.thirdPartyState.thirdPartyFieldConfigState,
					prevMappedThirdPartyFieldId,
				),
			);
		}
		setState(newState);
	};
